import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, Target } from "lucide-react";
import { PageBody, PageHeader, EmptyState } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FieldHelp, PageTip } from "@/components/FieldHelp";
import { HELP } from "@/lib/fieldHelp";
import api from "@/lib/api";

export default function Strategy() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [gen, setGen] = useState(false);
  const offerParam = params.get("offer");

  const load = async () => {
    const { data } = await api.get("/offers");
    setOffers(data);
    const pick = data.find((o) => o.id === offerParam) || data[0] || null;
    setSelected(pick);
    if (pick && pick.id !== offerParam) setParams({ offer: pick.id }, { replace: true });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (offerParam && offers.length) { const o = offers.find((x) => x.id === offerParam); if (o) setSelected(o); } }, [offerParam]);

  const changeOffer = (id) => { setParams({ offer: id }); setSelected(offers.find((o) => o.id === id)); };

  const generateIcp = async () => {
    if (!selected) return;
    setGen(true);
    try {
      const { data } = await api.post(`/offers/${selected.id}/generate-icp`);
      setSelected({ ...selected, icp: data });
      toast.success("ICP brief generated");
      load();
    } catch { toast.error("Generation failed"); }
    finally { setGen(false); }
  };

  const icp = selected?.icp;

  return (
    <PageBody>
      <PageHeader eyebrow="Strategy" title="ICP and messaging angles"
        subtitle="Generate a sharp ICP brief and three messaging angles from any offer. Every sequence is written from this — not the campaign name."
        actions={offers.length > 0 && (
          <div className="flex items-center gap-2">
            <Select value={selected?.id || ""} onValueChange={changeOffer}>
              <SelectTrigger className="w-[200px]" data-testid="strategy-offer-select"><SelectValue placeholder="Select an offer" /></SelectTrigger>
              <SelectContent>{offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.product}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={generateIcp} disabled={gen || !selected} data-testid="generate-icp-btn" className="rounded-md bg-amber-500 text-black hover:bg-amber-400 shrink-0">
              {gen ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4 mr-2" /> {icp ? "Regenerate" : "Generate ICP"}</>}
            </Button>
          </div>
        )}
      />

      <PageTip id="strategy">
        Pick an offer, then generate its ICP and angles. No offers yet? Create one under <span className="text-foreground font-medium">Offer</span>.
      </PageTip>

      {offers.length === 0 ? (
        <EmptyState icon={Target} title="No offers yet" body="Create an offer first — strategy is generated from it."
          action={<Button className="rounded-md bg-amber-500 text-black hover:bg-amber-400" onClick={() => navigate("/app/offer")} data-testid="strategy-create-offer">Go to Offer</Button>} />
      ) : selected ? (
        <div className="rounded-xl border border-border/70 bg-card/50 p-6" data-testid="strategy-panel">
          <div>
            <h3 className="font-display text-lg font-semibold tracking-tight">{selected.product}</h3>
            <p className="text-sm text-muted-foreground mt-1">{selected.features}</p>
          </div>

          {icp ? (
            <div className="mt-6 space-y-6" data-testid="icp-brief">
              <div className="rounded-lg bg-accent/40 border border-border/50 p-4">
                <div className="eyebrow mb-2 flex items-center gap-1.5">ICP Brief <FieldHelp title="ICP" testid="help-icp" {...HELP.icp} /></div>
                <p className="text-sm leading-relaxed">{icp.icp_brief}</p>
                {icp.prompt_version && <div className="mt-3 text-[10px] font-mono text-muted-foreground">prompt: {icp.prompt_version}</div>}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {[["Titles", icp.titles], ["Industries", icp.industries], ["Seniority", icp.seniority], ["Triggers", icp.triggers], ["Geography", icp.geography], ["Buying committee", icp.buying_committee]].map(([label, arr]) => (
                  <div key={label}>
                    <div className="text-xs text-muted-foreground mb-1.5">{label}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(arr || []).map((t, i) => <Badge key={`${label}-${i}`} variant="secondary" className="text-xs font-normal">{t}</Badge>)}
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <div className="eyebrow mb-3">Messaging angles</div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {(icp.angles || []).map((a, i) => (
                    <div key={a.name || i} className="rounded-lg border border-border/60 p-4">
                      <div className="font-medium text-sm text-amber-500">{a.name}</div>
                      <p className="text-xs text-muted-foreground mt-2"><span className="text-foreground">Problem:</span> {a.problem}</p>
                      <p className="text-xs text-muted-foreground mt-1.5"><span className="text-foreground">Insight:</span> {a.insight}</p>
                      <p className="text-xs text-muted-foreground mt-1.5"><span className="text-foreground">Proof:</span> {a.proof}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-dashed border-border/70 py-12 text-center text-sm text-muted-foreground">
              <div className="flex items-center justify-center gap-1.5">Generate an ICP brief and three messaging angles from this offer. <FieldHelp title="ICP" testid="help-icp-empty" {...HELP.icp} /></div>
            </div>
          )}
        </div>
      ) : null}
    </PageBody>
  );
}
